// src/components/Linking/TPPLinking.tsx
import React, { ComponentProps, useMemo, useState } from 'react'
import { Fade } from '@mui/material'
import LoadingBackdrop from '../../shared/components/LoadingBackdrop/LoadingBackdrop'

import LinkableItemsModal from './components/LinkableItems/LinkableItemsModal'
import { LinkingItem, LinkingItemsIds } from './Linking.types'
import { useGetLinkedItemsQuery } from '../../shared/queries/linking/useGetLinkedItemsQuery'
import { useUpdateLinkedItemsMutation } from '../../shared/queries/linking/useUpdateLinkedItemsMutation'

export type TPPLinkingProps = {
    id: LinkingItem['_id']
    type: LinkingItem['type']
    previousDataStack: unknown[] // kept for API compatibility, not used here
    onClose?: () => void // lets the parent close its dialog when done
    initialTab?: LinkingItem['type'] // optional tab to select on open
}

const TPPLinking = React.memo(
    ({ id, type, previousDataStack, onClose, initialTab }: TPPLinkingProps) => {
        const handleClose = () => {
            if (onClose) {
                onClose()
            }
        }

        const getLinkedItemsQuery = useGetLinkedItemsQuery(id, type)

        const [selectedTabId, setSelectedTabId] = useState<LinkingItem['type']>(initialTab ?? null)

        const updateLinkedItemsMutation = useUpdateLinkedItemsMutation({
            onSuccess: () => {
                handleClose()
            },
            onError: (error) => {
                console.error('Failed to update linked items:', error)
            },
        })

        // ---- Same data shaping as in Linking.tsx ----

        const evidenceItemsWithKeyEvidenceStatements = useMemo(() => {
            if (!getLinkedItemsQuery.data?.evidence) return []

            const evidenceItemsMap = getLinkedItemsQuery.data.evidence.reduce(
                (acc, evidenceItem) => {
                    return {
                        ...acc,
                        [evidenceItem._id]: evidenceItem,
                    }
                },
                {} as Record<LinkingItem['_id'], LinkingItem>
            )

            getLinkedItemsQuery.data.keyEvidenceStatement?.forEach((keyEvidenceStatementItem) => {
                if (!keyEvidenceStatementItem.extras.parent?._id) return
                const parentEvidence = { ...evidenceItemsMap[keyEvidenceStatementItem.extras.parent._id] }

                if (!parentEvidence.children) parentEvidence.children = []

                parentEvidence.children.push(keyEvidenceStatementItem)

                evidenceItemsMap[parentEvidence._id] = parentEvidence
            })

            return Object.values(evidenceItemsMap)
        }, [getLinkedItemsQuery.data])

        const linkedItems = useMemo(() => {
            const linkedItems = { ...getLinkedItemsQuery.data }

            if (linkedItems?.evidence) {
                linkedItems.evidence = evidenceItemsWithKeyEvidenceStatements
            }

            return linkedItems
        }, [evidenceItemsWithKeyEvidenceStatements])

        const handleSave = async (selectedIds: LinkingItemsIds) => {
            await updateLinkedItemsMutation.mutateAsync({
                id,
                type,
                selectedIds,
            })
        }

        if (getLinkedItemsQuery.isLoading) {
            return <LoadingBackdrop loading title="Linked data" subtitle="Fetching Linked Data" />
        }

        // ----------------------------------------------------------------
        // This is effectively "just the modal" you see AFTER clicking
        // the link/unlink button in the original Linking.tsx.
        // ----------------------------------------------------------------
        return (
            <Fade in timeout={300}>
                <div>
                    <LinkableItemsModal
                        type={type}
                        linkedItems={linkedItems}
                        handleClose={handleClose}
                        handleSave={handleSave}
                        isSaving={updateLinkedItemsMutation.isLoading}
                        selectedTabId={selectedTabId}
                        onTabChange={setSelectedTabId}
                    />
                </div>
            </Fade>
        )
    },
    (prevProps, nextProps) => {
        return prevProps.id === nextProps.id && prevProps.type === nextProps.type
    }
)

export default TPPLinking
